import { escapeUserText } from "../../utils/mentions";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  MapPin,
  Phone,
  Share2,
} from "lucide-react";
import { EventMediaDisplay } from "./eventMediaDisplay";
import EditCoolActivity from "./EditCoolActivity";
import "./Evenlistverticalview.css";

const createLinkifiedText = (text = "") =>
  escapeUserText(text).replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

const formatEventDate = (value) => {
  if (!value) return "TBA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatLocation = (event) => {
  const location = (event.location || "").trim();
  const country = (event.country || "").trim();
  if (location && country && location.toLowerCase() !== country.toLowerCase()) {
    return `${location}, ${country}`;
  }
  return location || country || "TBA";
};

const EventList = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [visibleCards, setVisibleCards] = useState({});
  const [scrollIndex, setScrollIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    message: "",
  });

  const carouselRef = useRef(null);
  const cardsRef = useRef([]);
  const API_URL = process.env.REACT_APP_API_URL;

  const loadEvents = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/get_event_activities.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setEvents(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching events:", err);
        setLoading(false);
      });
  }, [API_URL]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-id");
            setVisibleCards((prev) => ({ ...prev, [id]: true }));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach((card) => card && observer.observe(card));
    return () => observer.disconnect();
  }, [events]);

  useEffect(() => {
    if (!events.length) return undefined;
    const interval = setInterval(() => {
      scrollRight();
    }, 10000);
    return () => clearInterval(interval);
  }, [events, scrollIndex]);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const scrollToIndex = (index) => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    const card = cardsRef.current[index];
    if (!card) return;

    const containerWidth = container.offsetWidth;
    const cardWidth = card.offsetWidth;
    const targetLeft =
      card.offsetLeft - (containerWidth / 2 - cardWidth / 2);
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const clampedLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));

    container.scrollTo({ left: clampedLeft, behavior: "smooth" });
    setScrollIndex(index);
  };

  const scrollLeft = () => {
    if (!events.length) return;
    const newIndex = scrollIndex - 1 < 0 ? events.length - 1 : scrollIndex - 1;
    scrollToIndex(newIndex);
  };

  const scrollRight = () => {
    if (!events.length) return;
    const newIndex = scrollIndex + 1 >= events.length ? 0 : scrollIndex + 1;
    scrollToIndex(newIndex);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.contact || !formData.message) {
      alert("Please fill out all fields.");
      return;
    }
    try {
      const response = await fetch(`${API_URL}/contact_submit.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) {
        alert("Thank you for reaching out! We'll get back to you soon.");
        setFormData({ name: "", email: "", contact: "", message: "" });
        setShowContactModal(false);
      } else {
        alert("Error: " + result.message);
      }
    } catch (err) {
      console.error("Contact form submission failed:", err);
      alert("Something went wrong. Please try again later.");
    }
  };

  const handleShare = (event) => {
    const shareUrl = `${window.location.origin}/events/${event.id}`;
    if (navigator.share) {
      navigator
        .share({
          title: event.event_name,
          text: `Check this event: ${event.event_name}`,
          url: shareUrl,
        })
        .catch(() => window.open(shareUrl, "_blank"));
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Link copied to clipboard!");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      const res = await fetch(`${API_URL}/delete_event.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: eventId }),
      });
      const data = await res.json();
      if (data.success) {
        alert("Event deleted successfully.");
        setEvents((prev) => prev.filter((e) => e.id !== eventId));
      } else {
        alert("Failed to delete event: " + data.message);
      }
    } catch (err) {
      console.error("Error deleting event:", err);
      alert("Something went wrong while deleting.");
    }
  };

  if (loading) {
    return (
      <div className="es-loader">
        <div className="es-spinner" />
        <p>Loading events...</p>
      </div>
    );
  }

  const dotCount = Math.min(events.length, 4);
  const activeDot = events.length
    ? Math.min(scrollIndex, Math.max(dotCount - 1, 0))
    : 0;

  return (
    <section className="es-section">
      <div className="es-inner">
        <header className="es-header">
          <div className="es-badge">EVENT SERIES</div>
          <h2 className="es-title">
            Series of <span className="es-title-accent">Expert Speakers</span>
          </h2>
          <p className="es-subtitle">
            Thought leadership, hands-on insights, and real-world
            expertise—delivered to you.
          </p>
        </header>

        <div className="es-carousel-wrap">
          <button
            type="button"
            className="es-arrow"
            onClick={scrollLeft}
            aria-label="Previous events"
          >
            <ChevronLeft size={22} strokeWidth={2.4} />
          </button>

          <div ref={carouselRef} className="es-carousel">
            {events.length > 0 ? (
              events.map((event, index) => {
                const isExpanded = expanded[event.id] || false;
                const isVisible = visibleCards[event.id] || false;
                const fullDescription = event.description || "";
                const descriptionText = isExpanded
                  ? fullDescription
                  : fullDescription.slice(0, 100);

                return (
                  <article
                    key={event.id}
                    data-id={event.id}
                    ref={(el) => {
                      cardsRef.current[index] = el;
                    }}
                    className={`es-card${isVisible ? " is-visible" : ""}`}
                  >
                    <div className="es-media">
                      <EventMediaDisplay
                        event={event}
                        apiUrl={API_URL}
                        mediaStyle={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        noMediaContent={
                          <div className="es-media-empty">No Media</div>
                        }
                      />
                    </div>

                    <div className="es-body">
                      <h3 className="es-card-title">{event.event_name}</h3>

                      <p
                        className="es-card-desc"
                        style={
                          isExpanded
                            ? {
                                display: "block",
                                WebkitLineClamp: "unset",
                                minHeight: "auto",
                              }
                            : undefined
                        }
                        dangerouslySetInnerHTML={{
                          __html:
                            createLinkifiedText(descriptionText) +
                            (fullDescription.length > 100 && !isExpanded
                              ? "..."
                              : ""),
                        }}
                      />

                      {fullDescription.length > 100 && (
                        <button
                          type="button"
                          className="es-see-more"
                          onClick={() => toggleExpand(event.id)}
                        >
                          {isExpanded ? "See Less" : "See More"}
                        </button>
                      )}

                      <div className="es-meta">
                        <span className="es-meta-item">
                          <Calendar size={14} strokeWidth={2.2} />
                          {formatEventDate(event.event_date)}
                        </span>
                        <span className="es-meta-item">
                          <MapPin size={14} strokeWidth={2.2} />
                          {formatLocation(event)}
                        </span>
                      </div>

                      <div className="es-actions">
                        <Link
                          to={`/events/${event.id}`}
                          className="es-btn es-btn-outline"
                        >
                          <Eye size={14} strokeWidth={2.2} />
                          View Details
                        </Link>
                        <button
                          type="button"
                          className="es-btn es-btn-solid"
                          onClick={() => setShowContactModal(true)}
                        >
                          <Phone size={14} strokeWidth={2.2} />
                          Contact Us
                        </button>
                        <button
                          type="button"
                          className="es-btn es-btn-outline"
                          onClick={() => handleShare(event)}
                        >
                          <Share2 size={14} strokeWidth={2.2} />
                          Share
                        </button>
                      </div>

                      <div className="es-admin-actions">
                        <button
                          type="button"
                          className="es-btn-edit"
                          onClick={() => setEditingEvent(event)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="es-btn-delete"
                          onClick={() => handleDelete(event.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })
            ) : (
              <p className="es-empty">No events found.</p>
            )}
          </div>

          <button
            type="button"
            className="es-arrow"
            onClick={scrollRight}
            aria-label="Next events"
          >
            <ChevronRight size={22} strokeWidth={2.4} />
          </button>
        </div>

        {events.length > 1 && (
          <div className="es-dots" role="tablist" aria-label="Event slides">
            {Array.from({ length: dotCount }).map((_, index) => (
              <button
                key={index}
                type="button"
                className={`es-dot${activeDot === index ? " is-active" : ""}`}
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => scrollToIndex(index)}
              />
            ))}
          </div>
        )}

        {events.length > 0 && (
          <div className="es-view-all-wrap">
            <Link to="/events" className="es-view-all">
              <Calendar size={18} strokeWidth={2.2} />
              View All Events
              <ChevronRight size={18} strokeWidth={2.4} />
            </Link>
          </div>
        )}
      </div>

      {editingEvent && (
        <EditCoolActivity
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onUpdated={loadEvents}
        />
      )}

      {showContactModal && (
        <div className="es-modal-overlay">
          <div className="es-modal">
            <h3>Contact Us</h3>
            <form onSubmit={handleSubmitContact} className="es-form">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="contact"
                placeholder="Contact Number"
                value={formData.contact}
                onChange={handleInputChange}
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
              />
              <div className="es-modal-actions">
                <button type="submit" className="es-modal-submit">
                  Submit
                </button>
                <button
                  type="button"
                  className="es-modal-cancel"
                  onClick={() => setShowContactModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default EventList;
